import { useEffect, useState, useCallback } from "react";
import api, { inr, downloadPdf } from "@/lib/api";
import NewOrderDialog from "@/components/NewOrderDialog";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, FileDown, Trash2, ShoppingBag, IndianRupee, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function OrdersTab({ onChanged }) {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);
  const [custId, setCustId] = useState("all");
  const [duesOnly, setDuesOnly] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      if (custId !== "all") params.set("customer_id", custId);
      const r = await api.get(`/orders?${params.toString()}`);
      setOrders(r.data);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, custId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [showNew]);

  const removeOrder = async (id) => {
    if (!window.confirm("Do you want to delete this order?")) return;
    await api.delete(`/orders/${id}`);
    toast.success("Order deleted");
    load();
    onChanged?.();
  };

  const [paying, setPaying] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState("");
  const [custPayOpen, setCustPayOpen] = useState(false);
  const [custPayAmount, setCustPayAmount] = useState("");
  const [custPayDate, setCustPayDate] = useState("");

  const openPay = (o) => {
    setPaying(o);
    setPayAmount(o.received ? String(o.received) : "");
    setPayDate(o.received_date || new Date().toISOString().slice(0, 10));
  };

  const savePayment = async (fullAmount) => {
    const amt = fullAmount != null ? fullAmount : Number(payAmount || 0);
    try {
      await api.put(`/orders/${paying.id}/payment`, { amount: amt, date: payDate });
      toast.success("Payment saved");
      setPaying(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment save failed");
    }
  };

  const openCustPay = () => {
    setCustPayAmount(filteredDue > 0 ? String(Math.round(filteredDue * 100) / 100) : "");
    setCustPayDate(todayStr);
    setCustPayOpen(true);
  };

  const saveCustPay = async () => {
    const amt = Number(custPayAmount || 0);
    try {
      const r = await api.post(`/customers/${custId}/receive`, { amount: amt, date: custPayDate });
      toast.success(`${inr(r.data.applied)} received — ${r.data.orders_updated} order(s) par adjust hua${r.data.unapplied > 0.009 ? ` (${inr(r.data.unapplied)} extra, due se zyada tha)` : ""}`);
      setCustPayOpen(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment save failed");
    }
  };

  const reportPdf = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set("from_date", fromDate);
    if (toDate) params.set("to_date", toDate);
    if (custId !== "all") params.set("customer_id", custId);
    if (duesOnly) params.set("due_only", "1");
    downloadPdf(`/reports/orders/pdf?${params.toString()}`, "orders-report.pdf");
  };

  const displayOrders = duesOnly ? orders.filter((o) => (o.total || 0) - (o.received || 0) > 0) : orders;
  const filteredTotal = displayOrders.reduce((s, o) => s + o.total, 0);
  const filteredReceived = displayOrders.reduce((s, o) => s + (o.received || 0), 0);
  const filteredDue = filteredTotal - filteredReceived;

  return (
    <div className="space-y-4" data-testid="orders-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">From</label>
          <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setDuesOnly(false); }} data-testid="orders-from-date" className="w-40" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">To</label>
          <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setDuesOnly(false); }} data-testid="orders-to-date" className="w-40" />
        </div>
        <div>
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Customer</label>
          <Select value={custId} onValueChange={setCustId}>
            <SelectTrigger className="w-44" data-testid="orders-customer-filter">
              <SelectValue placeholder="All Customer" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customer</SelectItem>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}{c.firm ? ` (${c.firm})` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setDuesOnly(false); setFromDate(todayStr); setToDate(todayStr); setCustId("all"); }} data-testid="orders-today-btn" className={duesOnly ? "text-muted-foreground hover:bg-slate-100 font-semibold" : "text-cyan-700 hover:bg-cyan-50 font-semibold"}>
          Today
        </Button>
        <Button variant="ghost" size="sm" onClick={() => { setDuesOnly(true); setFromDate(""); setToDate(""); }} data-testid="orders-dues-btn" className={duesOnly ? "text-red-600 bg-red-50 font-semibold" : "text-muted-foreground hover:bg-red-50 hover:text-red-600 font-semibold"}>
          Dues
        </Button>
        <div className="flex-1" />
        <Button variant="outline" onClick={reportPdf} data-testid="report-pdf-btn" className="gap-1.5">
          <FileDown className="h-4 w-4" /> Report PDF
        </Button>
        <Button onClick={() => setShowNew(true)} data-testid="order-create-btn" className="gap-1.5 bg-cyan-600 hover:bg-cyan-700 text-white">
          <Plus className="h-4 w-4" /> New Order
        </Button>
      </div>

      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <p className="font-heading font-semibold text-sm" data-testid="orders-count">
            {displayOrders.length} Order{displayOrders.length === 1 ? "" : "s"}{duesOnly ? " (Dues)" : ""}
          </p>
          <div className="flex items-center gap-3">
            {custId !== "all" && filteredDue > 0 && (
              <Button size="sm" variant="outline" onClick={openCustPay} data-testid="customer-receive-btn" className="gap-1.5 h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50 font-semibold">
                <IndianRupee className="h-3.5 w-3.5" /> Receive from Customer
              </Button>
            )}
            <p className="text-sm font-semibold" data-testid="orders-filtered-total">
              Total: <span className="text-cyan-700">{inr(filteredTotal)}</span>
              <span className="text-muted-foreground font-normal"> · </span>
              Received: <span className="text-emerald-600">{inr(filteredReceived)}</span>
              <span className="text-muted-foreground font-normal"> · </span>
              Due: <span className="text-red-500">{inr(filteredDue)}</span>
            </p>
          </div>
        </div>
        {loading ? (
          <Loading text="Loading orders..." />
        ) : displayOrders.length === 0 ? (
          <div className="p-10 text-center" data-testid="orders-empty">
            <ShoppingBag className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-muted-foreground text-sm font-semibold tracking-wide">ORDER NOT AVAILABLE</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="orders-table">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Date</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Customer</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500">Products</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Amount</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Received</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Due</th>
                  <th className="px-4 py-2.5 font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayOrders.map((o) => (
                  <tr key={o.id} className="border-t border-border hover:bg-slate-50/60" data-testid={`order-row-${o.id.slice(0, 8)}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-medium">{o.order_date}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{o.customer_name}</p>
                      {o.customer_firm && <p className="text-xs text-muted-foreground">{o.customer_firm}</p>}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground max-w-xs">
                      {o.items.map((it) => `${it.product_name} x${it.qty}${it.note ? ` (${it.note})` : ""}`).join(", ")}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{inr(o.total)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600">
                      {inr(o.received || 0)}
                      {o.received_date ? <span className="block text-[10px] text-muted-foreground font-normal">{o.received_date}</span> : null}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-red-500">{inr((o.total || 0) - (o.received || 0))}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setEditingOrder(o)} data-testid={`order-edit-btn-${o.id.slice(0, 8)}`} className="text-slate-600 hover:text-slate-800 hover:bg-slate-100" title="Edit Order">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openPay(o)} data-testid={`order-receive-btn-${o.id.slice(0, 8)}`} className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" title="Receive Payment">
                        <IndianRupee className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(`/orders/${o.id}/pdf`, `order_${o.id.slice(0, 8)}.pdf`)} data-testid={`order-invoice-btn-${o.id.slice(0, 8)}`} className="text-cyan-700 hover:text-cyan-800 hover:bg-cyan-50">
                        <FileText className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeOrder(o.id)} data-testid={`order-delete-btn-${o.id.slice(0, 8)}`} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewOrderDialog
        open={showNew || !!editingOrder}
        editOrder={editingOrder}
        onClose={() => { setShowNew(false); setEditingOrder(null); }}
        customers={customers}
        products={products}
        onSaved={(keepOpen) => { if (!keepOpen) { setShowNew(false); setEditingOrder(null); } load(); onChanged?.(); }}
      />

      <Dialog open={!!paying} onOpenChange={(v) => !v && setPaying(null)}>
        <DialogContent className="max-w-sm" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Receive Amount</DialogTitle>
          </DialogHeader>
          {paying && (
            <div className="space-y-3">
              <p className="text-sm">{paying.customer_name} — Order Total: <b>{inr(paying.total)}</b></p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Amount Received</label>
                <Input type="number" min="0" step="any" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} data-testid="payment-amount-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Received Date</label>
                <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date-input" />
              </div>
              <Button variant="outline" className="w-full" onClick={() => savePayment(paying.total)} data-testid="payment-full-btn">
                Full Amount {inr(paying.total)}
              </Button>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPaying(null)}>Cancel</Button>
                <Button onClick={() => savePayment()} data-testid="payment-save-btn" className="bg-cyan-600 hover:bg-cyan-700 text-white">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={custPayOpen} onOpenChange={setCustPayOpen}>
        <DialogContent className="max-w-sm" data-testid="customer-receive-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Receive from Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">{customers.find((c) => c.id === custId)?.name} — Total Due: <b className="text-red-500">{inr(filteredDue)}</b></p>
            <p className="text-xs text-muted-foreground">Amount sabse purane due order se adjust hoga (FIFO).</p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Amount Received</label>
              <Input type="number" min="0" step="any" value={custPayAmount} onChange={(e) => setCustPayAmount(e.target.value)} data-testid="customer-receive-amount" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Received Date</label>
              <Input type="date" value={custPayDate} onChange={(e) => setCustPayDate(e.target.value)} data-testid="customer-receive-date" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCustPayOpen(false)}>Cancel</Button>
              <Button onClick={saveCustPay} data-testid="customer-receive-save-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
